import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FileText, Receipt, Filter, Eye, X, Image as ImageIcon, BookOpen, Users, Fuel, ChevronDown, ChevronRight, Briefcase, MapPin } from "lucide-react";
import { Button, Card, Badge, Input, Select, Spinner } from "../shared/ui";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU') : '-';

const TABS = [
  { key: 'receipts',   label: 'Receipts',        icon: Receipt },
  { key: 'photos',     label: 'Machine Photos',  icon: ImageIcon },
  { key: 'dockets',    label: 'Day Dockets',     icon: BookOpen },
  { key: 'forms',      label: 'Forms',           icon: FileText },
  { key: 'by-client',  label: 'Jobs by Client',  icon: Users }
];

const RecordsPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('receipts');
  const [loading, setLoading] = useState(true);

  const [receipts, setReceipts] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [dockets, setDockets] = useState(null);
  const [forms, setForms] = useState(null);
  const [clientsData, setClientsData] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);

  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const [filters, setFilters] = useState({
    job_id: '', user_id: '', start_date: '', end_date: '',
    source: '', category: '', machine_id: '', photo_type: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.job_id) params.job_id = filters.job_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      if (tab === 'receipts') {
        if (filters.user_id) params.user_id = filters.user_id;
        if (filters.source) params.source = filters.source;
        const res = await axios.get(`${API}/records/receipts`, { params });
        setReceipts(res.data);
      } else if (tab === 'photos') {
        if (filters.machine_id) params.machine_id = filters.machine_id;
        if (filters.photo_type) params.photo_type = filters.photo_type;
        const res = await axios.get(`${API}/records/machine-photos`, { params });
        setPhotos(res.data);
      } else if (tab === 'dockets') {
        const q = {};
        if (filters.job_id) q.job_id = filters.job_id;
        // Backend /api/daybook supports job_id+status; fetch all, filter client-side by date
        const res = await axios.get(`${API}/daybook`, { params: q }).catch(() => ({ data: [] }));
        let list = Array.isArray(res.data) ? res.data : [];
        if (filters.start_date) list = list.filter(d => (d.work_date || '') >= filters.start_date);
        if (filters.end_date)   list = list.filter(d => (d.work_date || '') <= filters.end_date);
        setDockets(list);
      } else if (tab === 'forms') {
        if (filters.user_id) params.user_id = filters.user_id;
        if (filters.category) params.category = filters.category;
        const res = await axios.get(`${API}/records/forms`, { params });
        setForms(res.data);
      } else if (tab === 'by-client') {
        const res = await axios.get(`${API}/records/jobs-by-client`);
        setClientsData(res.data);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchMeta = async () => {
    try {
      const [j, u, m] = await Promise.all([
        axios.get(`${API}/jobs`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/machines`).catch(() => ({ data: [] }))
      ]);
      setJobs(j.data);
      setUsers(u.data);
      setMachines(m.data);
    } catch (e) {}
  };

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [tab, filters]);

  const resetFilters = () => setFilters({
    job_id: '', user_id: '', start_date: '', end_date: '',
    source: '', category: '', machine_id: '', photo_type: ''
  });

  return (
    <div className="space-y-4" data-testid="records-page">
      <div>
        <h1 className="text-2xl font-bold uppercase">Records</h1>
        <p className="text-slate-500 mt-1">All receipts, photos, dockets and forms — filter by job, date, machine or person</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b-2 border-slate-200 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`records-tab-${t.key}`}
              className={`px-3 py-2 font-bold text-xs uppercase -mb-[2px] border-b-2 flex items-center gap-2 whitespace-nowrap ${
                active
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters (hide for jobs-by-client) */}
      {tab !== 'by-client' && (
        <Card className="bg-slate-50" data-testid="records-filters">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold uppercase">Filters</h3>
            <Button size="sm" variant="ghost" onClick={resetFilters} className="ml-auto" data-testid="reset-filters-btn">Clear</Button>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            <Select
              label="Job"
              value={filters.job_id}
              onChange={(e) => setFilters(f => ({ ...f, job_id: e.target.value }))}
              options={[{ value: '', label: 'All jobs' }, ...jobs.map(j => ({ value: j.id, label: j.title }))]}
              testId="filter-job"
            />
            <Input
              label="From"
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value }))}
              testId="filter-start-date"
            />
            <Input
              label="To"
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value }))}
              testId="filter-end-date"
            />
            {(tab === 'receipts' || tab === 'forms') && (
              <Select
                label="Person"
                value={filters.user_id}
                onChange={(e) => setFilters(f => ({ ...f, user_id: e.target.value }))}
                options={[{ value: '', label: 'Everyone' }, ...users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))]}
                testId="filter-user"
              />
            )}
            {tab === 'receipts' && (
              <Select
                label="Source"
                value={filters.source}
                onChange={(e) => setFilters(f => ({ ...f, source: e.target.value }))}
                options={[
                  { value: '', label: 'All sources' },
                  { value: 'fuel', label: 'Fuel stops' },
                  { value: 'reimbursement', label: 'Reimbursements' },
                  { value: 'daybook', label: 'Daybook/Machine' }
                ]}
                testId="filter-source"
              />
            )}
            {tab === 'photos' && (
              <>
                <Select
                  label="Machine"
                  value={filters.machine_id}
                  onChange={(e) => setFilters(f => ({ ...f, machine_id: e.target.value }))}
                  options={[{ value: '', label: 'All machines' }, ...machines.map(m => ({ value: m.id, label: m.name }))]}
                  testId="filter-machine"
                />
                <Select
                  label="Photo type"
                  value={filters.photo_type}
                  onChange={(e) => setFilters(f => ({ ...f, photo_type: e.target.value }))}
                  options={[
                    { value: '', label: 'All types' },
                    { value: 'condition', label: 'Condition' },
                    { value: 'damage', label: 'Damage' },
                    { value: 'general', label: 'General' },
                    { value: 'daybook_receipt', label: 'Daybook receipt' },
                    { value: 'checkout', label: 'Hire checkout' },
                    { value: 'return', label: 'Hire return' }
                  ]}
                  testId="filter-photo-type"
                />
              </>
            )}
            {tab === 'forms' && (
              <Select
                label="Category"
                value={filters.category}
                onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
                options={[
                  { value: '', label: 'All categories' },
                  { value: 'prestart', label: 'Prestart' },
                  { value: 'swms', label: 'SWMS' },
                  { value: 'incident', label: 'Incident' },
                  { value: 'weekly_report', label: 'Weekly Report' }
                ]}
                testId="filter-category"
              />
            )}
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : tab === 'receipts' ? (
        <ReceiptsView data={receipts} onPreview={setPreviewReceipt} jobs={jobs} user={user} onRefresh={fetchData} />
      ) : tab === 'photos' ? (
        <PhotosView data={photos} onPreview={setPreviewPhoto} jobs={jobs} />
      ) : tab === 'dockets' ? (
        <DocketsView data={dockets} />
      ) : tab === 'forms' ? (
        <FormsView data={forms} onPreview={setPreviewForm} />
      ) : (
        <JobsByClientView data={clientsData} />
      )}

      {/* Receipt preview modal */}
      {previewReceipt && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setPreviewReceipt(null)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="receipt-preview-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{(previewReceipt.expense_type || '').toUpperCase()} — {fmtMoney(previewReceipt.amount)}</h3>
              <button onClick={() => setPreviewReceipt(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm mb-3">
              <div><strong>Date:</strong> {fmtDate(previewReceipt.date)}</div>
              <div><strong>By:</strong> {previewReceipt.user_name}</div>
              <div><strong>Source:</strong> <Badge variant={previewReceipt.source === 'fuel' ? 'warning' : previewReceipt.source === 'daybook' ? 'warning' : 'info'}>{previewReceipt.source}</Badge></div>
              {previewReceipt.description && <div><strong>Description:</strong> {previewReceipt.description}</div>}
              {previewReceipt.reference && <div><strong>Reference:</strong> {previewReceipt.reference}</div>}
              {previewReceipt.machine_name && <div><strong>Machine:</strong> {previewReceipt.machine_name}</div>}
              {previewReceipt.client_name && <div><strong>Client:</strong> {previewReceipt.client_name}</div>}
              {previewReceipt.fuel_amount_l && <div><strong>Fuel:</strong> {previewReceipt.fuel_amount_l}L · {previewReceipt.payment_type}</div>}
              {previewReceipt.km_at_stop && <div><strong>KM at stop:</strong> {previewReceipt.km_at_stop}</div>}
              {previewReceipt.no_receipt_reason && (
                <div className="p-2 bg-red-50 text-red-700 rounded text-xs"><strong>No receipt:</strong> {previewReceipt.no_receipt_reason}</div>
              )}
            </div>
            {previewReceipt.receipt_url ? (
              <div className="mt-3">
                <img src={previewReceipt.receipt_url} alt="Receipt" className="w-full rounded border max-h-[60vh] object-contain bg-slate-50" />
              </div>
            ) : (
              <div className="mt-3 p-6 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 text-center" data-testid="receipt-no-photo">
                <Receipt className="w-10 h-10 mx-auto text-amber-500 mb-2" />
                <p className="text-sm font-bold text-amber-900 uppercase">No receipt photo uploaded</p>
                <p className="text-xs text-amber-700 mt-1">
                  {previewReceipt.no_receipt_reason
                    ? `Reason: ${previewReceipt.no_receipt_reason}`
                    : previewReceipt.source === 'fuel'
                      ? 'This fuel stop was logged without attaching a receipt photo.'
                      : 'The person who logged this entry didn\'t attach a photo.'}
                </p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Photo preview modal */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setPreviewPhoto(null)}>
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="photo-preview-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{previewPhoto.machine_name || 'Photo'} — {previewPhoto.photo_type || '—'}</h3>
              <button onClick={() => setPreviewPhoto(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-1 text-sm mb-3">
              <div><strong>Date:</strong> {fmtDate(previewPhoto.date)}</div>
              <div><strong>Source:</strong> <Badge variant="info">{previewPhoto.source?.replace('_', ' ')}</Badge></div>
              {previewPhoto.description && <div><strong>Notes:</strong> {previewPhoto.description}</div>}
              {previewPhoto.uploaded_by && <div><strong>By:</strong> {previewPhoto.uploaded_by}</div>}
              {previewPhoto.hirer && <div><strong>Hirer:</strong> {previewPhoto.hirer}</div>}
              {previewPhoto.amount ? <div><strong>Amount:</strong> {fmtMoney(previewPhoto.amount)}</div> : null}
            </div>
            {previewPhoto.photo_url ? (
              <img src={previewPhoto.photo_url} alt="Machine" className="w-full rounded border max-h-[70vh] object-contain bg-slate-50" />
            ) : (
              <p className="text-xs text-slate-400 italic">No image.</p>
            )}
          </Card>
        </div>
      )}

      {/* Form preview modal */}
      {previewForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setPreviewForm(null)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="form-preview-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{previewForm.template_name}</h3>
              <button onClick={() => setPreviewForm(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div><strong>Submitted by:</strong> {previewForm.user_name}</div>
              <div><strong>At:</strong> {previewForm.submitted_at ? new Date(previewForm.submitted_at).toLocaleString('en-AU') : '-'}</div>
              {previewForm.job_title && <div><strong>Job:</strong> {previewForm.job_title}</div>}
              {previewForm.template_category && <div><strong>Category:</strong> {previewForm.template_category}</div>}
              <div className="pt-2 border-t">
                <strong>Filled fields:</strong>
                <div className="mt-2 space-y-1">
                  {Object.entries(previewForm.data || {}).map(([k, v]) => (
                    <div key={k} className="p-2 bg-slate-50 rounded text-xs">
                      <span className="font-bold">{k}:</span> {typeof v === 'string' ? v : JSON.stringify(v)}
                    </div>
                  ))}
                </div>
              </div>
              {previewForm.required_signatures?.length > 0 && (
                <div className="pt-2 border-t">
                  <strong>Signatures:</strong>
                  <div className="mt-2 space-y-1">
                    {previewForm.required_signatures.map(s => (
                      <div key={s.id} className="flex items-center gap-2 text-xs">
                        <span>{s.name}</span>
                        <Badge variant={s.signed ? 'success' : 'warning'}>{s.signed ? 'Signed' : 'Pending'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {previewForm.attachments?.length > 0 && (
                <div className="pt-2 border-t">
                  <strong>Attachments:</strong> {previewForm.attachments.length}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const ReceiptsView = ({ data, onPreview, jobs, user, onRefresh }) => {
  const [groupBy, setGroupBy] = useState('date'); // 'date' | 'job' | 'user'
  const [reimbFilter, setReimbFilter] = useState('all'); // all | needs | reimbursed
  const [busyId, setBusyId] = useState(null);
  const canMarkPaid = ['admin', 'manager', 'office_staff'].includes(user?.role);

  if (!data || data.total === 0) return <Card className="text-center py-12"><p className="text-slate-500">No receipts match these filters</p></Card>;

  const jobTitle = (id) => jobs.find(j => j.id === id)?.title || 'Unassigned';

  const markPaid = async (r, newStatus) => {
    setBusyId(`${r.source}-${r.id}`);
    try {
      const { toast } = await import('sonner');
      const payload = { source: r.source, ref_id: r.id, status: newStatus };
      if (r.source === 'fuel') payload.travel_entry_id = r.travel_entry_id;
      if (r.source === 'daybook') payload.usage_id = r.usage_id;
      if (r.source === 'reimbursement') payload.timesheet_id = r.timesheet_id;
      await axios.post(`${API}/reimbursements/mark-paid`, payload);
      toast.success(newStatus === 'reimbursed' ? 'Marked as reimbursed' : 'Marked as pending');
      if (onRefresh) onRefresh();
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = data.receipts.filter(r => {
    if (reimbFilter === 'needs') return r.needs_reimbursement && r.reimbursement_status !== 'reimbursed';
    if (reimbFilter === 'reimbursed') return r.reimbursement_status === 'reimbursed';
    return true;
  });

  // group
  const groups = {};
  filtered.forEach(r => {
    let key;
    if (groupBy === 'date') key = r.date || 'Unknown';
    else if (groupBy === 'user') key = r.user_name || 'Unknown';
    else key = jobTitle(r.job_id);
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => (groupBy === 'date' ? b.localeCompare(a) : a.localeCompare(b)));

  return (
    <div className="space-y-3" data-testid="receipts-view">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{data.total}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Receipts</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-green-600">{fmtMoney(data.total_amount)}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Total Amount</p>
        </Card>
        <Card className="text-center p-3 border-2 border-amber-300 bg-amber-50" data-testid="owed-to-workers-card">
          <p className="text-2xl font-bold text-amber-700">{fmtMoney(data.needs_reimbursement_amount)}</p>
          <p className="text-[10px] text-amber-800 uppercase font-bold">Owed to Workers</p>
          <p className="text-[10px] text-amber-700">{data.needs_reimbursement_count || 0} pending</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-sm font-bold">{data.sources.reimbursement} reimb · {data.sources.daybook} daybook</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">By Source</p>
        </Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="receipt-reimb-filter">
        <span className="text-xs font-bold uppercase text-slate-500">Show:</span>
        {[
          { k: 'all', label: 'All' },
          { k: 'needs', label: `Needs Reimbursement (${data.needs_reimbursement_count || 0})` },
          { k: 'reimbursed', label: 'Already Reimbursed' }
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setReimbFilter(t.k)}
            className={`px-3 py-1 rounded-full text-xs font-bold border ${reimbFilter === t.k ? 'bg-orange-500 text-slate-900 border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid={`reimb-filter-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase text-slate-500">Group by:</span>
        <Button size="sm" variant={groupBy === 'date' ? 'primary' : 'ghost'} onClick={() => setGroupBy('date')} data-testid="group-by-date">Date</Button>
        <Button size="sm" variant={groupBy === 'job' ? 'primary' : 'ghost'} onClick={() => setGroupBy('job')} data-testid="group-by-job">Job</Button>
        <Button size="sm" variant={groupBy === 'user' ? 'primary' : 'ghost'} onClick={() => setGroupBy('user')} data-testid="group-by-user">Person</Button>
      </div>

      <div className="space-y-3">
        {sortedKeys.map(key => {
          const items = groups[key];
          const sum = items.reduce((a, r) => a + (r.amount || 0), 0);
          const owed = items.filter(r => r.needs_reimbursement && r.reimbursement_status !== 'reimbursed').reduce((a, r) => a + (r.amount || 0), 0);
          return (
            <Card key={key} className="p-0 overflow-hidden" data-testid={`receipts-group-${key}`}>
              <div className="bg-slate-100 px-3 py-2 flex items-center justify-between border-b">
                <h4 className="font-bold text-sm uppercase">
                  {groupBy === 'date' ? fmtDate(key) : key}
                </h4>
                <div className="text-xs text-slate-600 flex items-center gap-3">
                  <span><span className="font-bold">{items.length}</span> receipt(s) · <span className="font-bold text-green-600">{fmtMoney(sum)}</span></span>
                  {owed > 0 && <span className="font-bold text-amber-700">Owed: {fmtMoney(owed)}</span>}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs">
                    <tr>
                      <th className="text-left p-2 font-bold">Source</th>
                      <th className="text-left p-2 font-bold">Type</th>
                      <th className="text-right p-2 font-bold">Amount</th>
                      <th className="text-left p-2 font-bold">By</th>
                      <th className="text-left p-2 font-bold">Description</th>
                      <th className="text-left p-2 font-bold">{groupBy === 'date' ? 'Job' : 'Date'}</th>
                      <th className="text-left p-2 font-bold">Reimburse</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(r => {
                      const rowKey = `${r.source}-${r.id}`;
                      const isReimbursed = r.reimbursement_status === 'reimbursed';
                      const needs = r.needs_reimbursement;
                      return (
                        <tr key={rowKey} className={`border-t border-slate-100 hover:bg-slate-50 ${needs && !isReimbursed ? 'bg-amber-50/40' : ''}`} data-testid={`receipt-row-${r.id}`}>
                          <td className="p-2"><Badge variant={r.source === 'fuel' ? 'warning' : r.source === 'daybook' ? 'warning' : 'info'}>{r.source}</Badge></td>
                          <td className="p-2 uppercase text-xs">{r.expense_type}</td>
                          <td className="p-2 text-right font-bold text-green-600">{fmtMoney(r.amount)}</td>
                          <td className="p-2 text-xs">{r.user_name}</td>
                          <td className="p-2 text-xs text-slate-600 max-w-[200px] truncate">{r.description || r.reference || '-'}</td>
                          <td className="p-2 text-xs">{groupBy === 'date' ? jobTitle(r.job_id) : fmtDate(r.date)}</td>
                          <td className="p-2 text-xs">
                            {needs ? (
                              <div className="flex items-center gap-1.5">
                                {isReimbursed ? (
                                  <Badge variant="success" data-testid={`reimb-status-${r.id}`}>Reimbursed</Badge>
                                ) : (
                                  <Badge variant="warning" data-testid={`reimb-status-${r.id}`}>Pending</Badge>
                                )}
                                {canMarkPaid && (
                                  <Button
                                    size="sm"
                                    variant={isReimbursed ? 'ghost' : 'primary'}
                                    onClick={() => markPaid(r, isReimbursed ? 'pending' : 'reimbursed')}
                                    disabled={busyId === rowKey}
                                    data-testid={`mark-reimbursed-${r.id}`}
                                  >
                                    {busyId === rowKey ? '...' : isReimbursed ? 'Undo' : 'Mark Paid'}
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-[11px]">Company card</span>
                            )}
                          </td>
                          <td className="p-2">
                            <Button size="sm" variant="ghost" onClick={() => onPreview(r)} data-testid={`preview-receipt-${r.id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const PhotosView = ({ data, onPreview, jobs }) => {
  const [groupBy, setGroupBy] = useState('date'); // 'date' | 'machine' | 'job'
  if (!data || data.total === 0) return <Card className="text-center py-12"><p className="text-slate-500">No photos match these filters</p></Card>;

  const jobTitle = (id) => jobs.find(j => j.id === id)?.title || 'Unassigned';

  const groups = {};
  data.photos.forEach(p => {
    let key;
    if (groupBy === 'date') key = p.date || 'Unknown';
    else if (groupBy === 'machine') key = p.machine_name || 'Unknown machine';
    else key = jobTitle(p.job_id);
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => (groupBy === 'date' ? b.localeCompare(a) : a.localeCompare(b)));

  return (
    <div className="space-y-3" data-testid="photos-view">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{data.total}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Photos</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-lg font-bold text-blue-600">{data.sources.machine_gallery || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Gallery</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-lg font-bold text-orange-600">{data.sources.daybook_receipt || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Daybook</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-lg font-bold text-purple-600">{(data.sources.hire_checkout || 0) + (data.sources.hire_return || 0)}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Hire In/Out</p>
        </Card>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase text-slate-500">Group by:</span>
        <Button size="sm" variant={groupBy === 'date' ? 'primary' : 'ghost'} onClick={() => setGroupBy('date')} data-testid="photos-group-date">Date</Button>
        <Button size="sm" variant={groupBy === 'machine' ? 'primary' : 'ghost'} onClick={() => setGroupBy('machine')} data-testid="photos-group-machine">Machine</Button>
        <Button size="sm" variant={groupBy === 'job' ? 'primary' : 'ghost'} onClick={() => setGroupBy('job')} data-testid="photos-group-job">Job</Button>
      </div>

      <div className="space-y-3">
        {sortedKeys.map(key => {
          const items = groups[key];
          return (
            <Card key={key} className="p-0 overflow-hidden" data-testid={`photos-group-${key}`}>
              <div className="bg-slate-100 px-3 py-2 flex items-center justify-between border-b">
                <h4 className="font-bold text-sm uppercase">
                  {groupBy === 'date' ? fmtDate(key) : key}
                </h4>
                <div className="text-xs text-slate-600"><span className="font-bold">{items.length}</span> photo(s)</div>
              </div>
              <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {items.map(p => (
                  <button
                    key={p.id}
                    onClick={() => onPreview(p)}
                    className="group relative rounded-lg border border-slate-200 overflow-hidden hover:border-orange-400 bg-slate-50 text-left"
                    data-testid={`photo-tile-${p.id}`}
                  >
                    {p.photo_url ? (
                      <img src={p.photo_url} alt="" className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center text-slate-400">
                        <ImageIcon className="w-8 h-8" />
                      </div>
                    )}
                    <div className="p-2 text-xs">
                      <div className="font-bold truncate">{p.machine_name || 'Machine'}</div>
                      <div className="text-slate-500 truncate">{p.photo_type || p.source}</div>
                      <div className="text-slate-400 text-[10px]">{fmtDate(p.date)}</div>
                    </div>
                    <span className="absolute top-1 right-1">
                      <Badge variant={p.source === 'daybook_receipt' ? 'warning' : p.source === 'machine_gallery' ? 'info' : 'default'}>
                        {p.source?.replace('_', ' ')}
                      </Badge>
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const DocketsView = ({ data }) => {
  const [expanded, setExpanded] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  if (!data || data.length === 0) return <Card className="text-center py-12"><p className="text-slate-500">No dockets match these filters</p></Card>;

  const filtered = statusFilter === 'all' ? data : data.filter(d => d.status === statusFilter);
  const counts = data.reduce((a, d) => { a[d.status || 'unknown'] = (a[d.status || 'unknown'] || 0) + 1; return a; }, {});

  // Group by job
  const groups = {};
  filtered.forEach(d => {
    const key = d.job_title || '(No job)';
    if (!groups[key]) groups[key] = { client: d.client_name, items: [] };
    groups[key].items.push(d);
  });
  const sortedKeys = Object.keys(groups).sort();

  const statusBadge = (s) => {
    if (s === 'completed') return 'success';
    if (s === 'in_progress' || s === 'in-progress') return 'warning';
    if (s === 'draft' || s === 'new') return 'info';
    if (s === 'cancelled') return 'error';
    return 'default';
  };

  const openPdf = (d) => {
    if (d.status !== 'completed') {
      alert('This docket is not completed yet — only completed dockets can be exported as a PDF. Open the docket in Daybook to finish signing.');
      return;
    }
    window.open(`${API}/daybook/${d.id}/pdf`, '_blank');
  };

  return (
    <div className="space-y-3" data-testid="dockets-view">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{data.length}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">All Dockets</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-blue-600">{counts.draft || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Draft</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-amber-600">{counts.in_progress || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">In Progress</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-green-600">{counts.completed || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Completed</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{sortedKeys.length}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Jobs</p>
        </Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase text-slate-500">Status:</span>
        {['all', 'draft', 'in_progress', 'completed', 'cancelled'].map(s => (
          <Button key={s} size="sm" variant={statusFilter === s ? 'primary' : 'ghost'} onClick={() => setStatusFilter(s)} data-testid={`dockets-status-${s}`}>
            {s.replace('_', ' ')}{s !== 'all' && ` (${counts[s] || 0})`}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        {sortedKeys.map(job => {
          const g = groups[job];
          const isOpen = expanded[job] !== false; // default open
          return (
            <Card key={job} className="p-0 overflow-hidden" data-testid={`dockets-job-${job}`}>
              <button
                onClick={() => setExpanded(e => ({ ...e, [job]: !isOpen }))}
                className="w-full bg-slate-100 px-3 py-2 flex items-center justify-between border-b hover:bg-slate-200"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <h4 className="font-bold text-sm uppercase">{job}</h4>
                  {g.client && <Badge variant="info">{g.client}</Badge>}
                </div>
                <span className="text-xs text-slate-600"><span className="font-bold">{g.items.length}</span> docket(s)</span>
              </button>
              {isOpen && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs">
                      <tr>
                        <th className="text-left p-2 font-bold">Work Date</th>
                        <th className="text-left p-2 font-bold">Status</th>
                        <th className="text-left p-2 font-bold">Description</th>
                        <th className="text-left p-2 font-bold">Labour/Equip</th>
                        <th className="text-left p-2 font-bold">Client Signed</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map(d => (
                        <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`docket-row-${d.id}`}>
                          <td className="p-2">{fmtDate(d.work_date)}</td>
                          <td className="p-2"><Badge variant={statusBadge(d.status)}>{(d.status || 'unknown').replace('_', ' ')}</Badge></td>
                          <td className="p-2 text-xs max-w-[260px] truncate">{d.description || d.work_type || '-'}</td>
                          <td className="p-2 text-xs">{(d.labour_entries?.length || 0)} / {(d.equipment_entries?.length || 0)}</td>
                          <td className="p-2 text-xs">{d.client_signature?.name || <span className="text-slate-400">—</span>}</td>
                          <td className="p-2">
                            <Button size="sm" variant="ghost" onClick={() => openPdf(d)} data-testid={`docket-pdf-${d.id}`}>
                              <Eye className="w-3.5 h-3.5 mr-1" /> {d.status === 'completed' ? 'PDF' : 'Locked'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="text-center py-8"><p className="text-slate-500 text-sm">No dockets with status "{statusFilter.replace('_', ' ')}"</p></Card>
        )}
      </div>
    </div>
  );
};

const JobsByClientView = ({ data }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  if (!data || data.total_clients === 0) return <Card className="text-center py-12"><p className="text-slate-500">No jobs yet</p></Card>;

  const statusColor = (s) => s === 'completed' ? 'success' : s === 'in_progress' ? 'warning' : s === 'cancelled' ? 'error' : 'info';
  const openJob = (id) => { navigate(`/jobs?open=${id}`); };

  return (
    <div className="space-y-3" data-testid="jobs-by-client-view">
      <div className="grid grid-cols-3 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{data.total_clients}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Clients</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-blue-600">{data.total_jobs}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Jobs</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-green-600">
            {fmtMoney(data.clients.reduce((a, c) => a + (c.total_contract || 0), 0))}
          </p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Total Contracts</p>
        </Card>
      </div>

      <div className="space-y-2">
        {data.clients.map(c => {
          const isOpen = expanded[c.client_name] !== false; // default open
          return (
            <Card key={c.client_name} className="p-0 overflow-hidden" data-testid={`client-${c.client_name}`}>
              <button
                onClick={() => setExpanded(e => ({ ...e, [c.client_name]: !isOpen }))}
                className="w-full bg-slate-100 px-3 py-2 flex items-center justify-between border-b hover:bg-slate-200"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Users className="w-4 h-4 text-slate-500" />
                  <h4 className="font-bold text-sm uppercase">{c.client_name}</h4>
                  <Badge variant="info">{c.jobs.length} job(s)</Badge>
                  {c.active_count > 0 && <Badge variant="warning">{c.active_count} active</Badge>}
                  {c.completed_count > 0 && <Badge variant="success">{c.completed_count} done</Badge>}
                </div>
                <span className="text-xs font-bold text-green-600">{fmtMoney(c.total_contract)}</span>
              </button>
              {isOpen && (
                <div className="divide-y divide-slate-100">
                  {c.jobs.map(j => (
                    <button
                      key={j.id}
                      onClick={() => openJob(j.id)}
                      className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3"
                      data-testid={`client-job-${j.id}`}
                    >
                      <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold truncate">{j.title}</span>
                          <Badge variant={statusColor(j.status)}>{(j.status || '').replace('_', ' ')}</Badge>
                        </div>
                        <div className="flex gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                          {j.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>}
                          {j.start_date && <span>Start: {fmtDate(j.start_date)}</span>}
                          {j.end_date && <span>End: {fmtDate(j.end_date)}</span>}
                        </div>
                      </div>
                      {j.contract_amount ? <span className="text-sm font-bold text-green-600">{fmtMoney(j.contract_amount)}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const FormsView = ({ data, onPreview }) => {
  if (!data || data.total === 0) return <Card className="text-center py-12"><p className="text-slate-500">No forms match these filters</p></Card>;
  return (
    <div className="space-y-3" data-testid="forms-view">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{data.total}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Forms</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-amber-600">{data.pending_signatures}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Pending Signatures</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-purple-600">{data.awaiting_approval}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Awaiting Approval</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-green-600">{data.approved}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Approved</p>
        </Card>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-white rounded-lg border">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-2 font-bold">Submitted</th>
              <th className="text-left p-2 font-bold">Form</th>
              <th className="text-left p-2 font-bold">Category</th>
              <th className="text-left p-2 font-bold">Job</th>
              <th className="text-left p-2 font-bold">By</th>
              <th className="text-left p-2 font-bold">Signatures</th>
              <th className="text-left p-2 font-bold">Approval</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.forms.map(f => (
              <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`form-row-${f.id}`}>
                <td className="p-2 text-xs">{f.submitted_at ? new Date(f.submitted_at).toLocaleDateString('en-AU') : '-'}</td>
                <td className="p-2 font-semibold">{f.template_name}</td>
                <td className="p-2 text-xs uppercase">{f.template_category || '-'}</td>
                <td className="p-2 text-xs">{f.job_title || <span className="text-slate-400">—</span>}</td>
                <td className="p-2 text-xs">{f.user_name}</td>
                <td className="p-2"><Badge variant={f.signature_status === 'complete' ? 'success' : 'warning'}>{f.signature_status || 'n/a'}</Badge></td>
                <td className="p-2">
                  <Badge variant={f.approval_status === 'approved' ? 'success' : f.approval_status === 'pending' ? 'warning' : 'default'}>
                    {f.approval_status || 'n/a'}
                  </Badge>
                </td>
                <td className="p-2">
                  <Button size="sm" variant="ghost" onClick={() => onPreview(f)} data-testid={`preview-form-${f.id}`}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecordsPage;
